import { useEffect, useState } from "react";
import api from "../lib/api";
import KpiCard from "../components/KpiCard";
import {
    ShoppingBag,
    CurrencyInr,
    ClockCounterClockwise,
    GraduationCap,
    TrendUp,
    CheckCircle,
    Truck,
    Coins,
} from "@phosphor-icons/react";
import { formatCurrency, formatDateTime } from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";
import {
    AreaChart,
    Area,
    ResponsiveContainer,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from "recharts";

export default function Dashboard() {
    const [summary, setSummary] = useState(null);
    const [recent, setRecent] = useState({ orders: [], payments: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([api.get("/dashboard/summary"), api.get("/dashboard/recent")])
            .then(([s, r]) => {
                setSummary(s.data);
                setRecent(r.data);
            })
            .catch(() => {
                /* interceptor handles 401 redirect; ignore transient errors */
            })
            .finally(() => setLoading(false));
    }, []);

    if (loading || !summary) {
        return (
            <div className="text-slate-400 text-sm" data-testid="dashboard-loading">
                Loading dashboard…
            </div>
        );
    }

    const o = summary.orders || {};
    const r = summary.revenue || {};
    const s = summary.students || {};

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <div>
                <div className="label-uppercase">Overview</div>
                <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">
                    Welcome back to Proxiq Command Center
                </h1>
                <p className="text-slate-400 mt-2 text-sm">
                    A live pulse of orders, revenue, payments and coaching activity.
                </p>
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <KpiCard
                    testId="kpi-total-orders"
                    label="Total Orders"
                    value={o.total || 0}
                    sub={`${o.pending || 0} pending · ${o.in_progress || 0} in progress`}
                    icon={ShoppingBag}
                    accent="from-blue-500/30 to-blue-500/0"
                />
                <KpiCard
                    testId="kpi-completed"
                    label="Completed"
                    value={(o.completed || 0) + (o.delivered || 0)}
                    sub={`${o.delivered || 0} delivered`}
                    icon={CheckCircle}
                    accent="from-emerald-500/30 to-emerald-500/0"
                />
                <KpiCard
                    testId="kpi-remaining"
                    label="Remaining"
                    value={o.remaining || 0}
                    sub={`${o.cancelled || 0} cancelled`}
                    icon={Truck}
                    accent="from-orange-500/30 to-orange-500/0"
                />
                <KpiCard
                    testId="kpi-revenue"
                    label="Total Revenue"
                    value={formatCurrency(r.credit_total || 0)}
                    sub={`Net: ${formatCurrency((r.credit_total || 0) - (r.debit_total || 0))}`}
                    icon={CurrencyInr}
                    accent="from-violet-500/30 to-violet-500/0"
                />
                <KpiCard
                    testId="kpi-pending-payments"
                    label="Pending Payments"
                    value={formatCurrency(r.pending_payments || 0)}
                    sub={`Advance: ${formatCurrency(o.advance_paid || 0)}`}
                    icon={Coins}
                    accent="from-amber-500/30 to-amber-500/0"
                />
                <KpiCard
                    testId="kpi-monthly-profit"
                    label="Monthly Profit"
                    value={formatCurrency(r.monthly_profit || 0)}
                    sub="Credits − Debits (this month)"
                    icon={TrendUp}
                    accent="from-cyan-500/30 to-cyan-500/0"
                />
                <KpiCard
                    testId="kpi-students"
                    label="Active Students"
                    value={s.active || 0}
                    sub={`${s.total || 0} total`}
                    icon={GraduationCap}
                    accent="from-fuchsia-500/30 to-fuchsia-500/0"
                />
                <KpiCard
                    testId="kpi-fees-pending"
                    label="Fees Pending"
                    value={formatCurrency(s.fees_pending || 0)}
                    sub={`Collected: ${formatCurrency(s.fees_paid || 0)}`}
                    icon={ClockCounterClockwise}
                    accent="from-rose-500/30 to-rose-500/0"
                />
            </div>

            {/* Trend chart + Recent */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="glass-card p-6 lg:col-span-2" data-testid="revenue-chart">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="label-uppercase">Revenue trend</div>
                            <div className="font-display text-xl font-semibold mt-1">
                                Last 6 months
                            </div>
                        </div>
                    </div>
                    <div style={{ width: "100%", height: 260 }}>
                        <ResponsiveContainer width="100%" height={260} minWidth={0}>
                            <AreaChart data={summary.trend || []}>
                                <defs>
                                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#2563EB" stopOpacity={0.55} />
                                        <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" vertical={false} />
                                <XAxis dataKey="month" stroke="#64748b" tickLine={false} axisLine={false} />
                                <YAxis stroke="#64748b" tickLine={false} axisLine={false} tickFormatter={(v) => `₹${v}`} />
                                <Tooltip
                                    contentStyle={{
                                        background: "rgba(15,23,42,0.95)",
                                        border: "1px solid rgba(255,255,255,0.1)",
                                        borderRadius: 12,
                                        color: "#f8fafc",
                                    }}
                                    formatter={(v) => formatCurrency(v)}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="revenue"
                                    stroke="#3B82F6"
                                    strokeWidth={2}
                                    fill="url(#rev)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="glass-card p-6" data-testid="status-breakdown">
                    <div className="label-uppercase">Order Pipeline</div>
                    <div className="font-display text-xl font-semibold mt-1">Current status</div>
                    <div className="mt-4 space-y-3">
                        {[
                            { k: "pending", v: o.pending || 0 },
                            { k: "in_progress", v: o.in_progress || 0 },
                            { k: "completed", v: o.completed || 0 },
                            { k: "delivered", v: o.delivered || 0 },
                            { k: "remaining", v: o.remaining || 0 },
                            { k: "cancelled", v: o.cancelled || 0 },
                        ].map((row) => (
                            <div key={row.k} className="flex items-center justify-between">
                                <StatusBadge status={row.k} />
                                <div className="font-display font-bold text-lg">{row.v}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="glass-card p-6" data-testid="recent-orders">
                    <div className="label-uppercase">Recent Orders</div>
                    <div className="mt-3 divide-y divide-white/5">
                        {(recent.orders || []).map((o) => (
                            <div key={o.id} className="py-3 flex items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm truncate">{o.client_name}</div>
                                    <div className="text-xs text-slate-500">
                                        {o.order_no} · {o.service_name || "—"}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display font-semibold text-sm">
                                        {formatCurrency(o.amount)}
                                    </div>
                                    <StatusBadge status={o.status} />
                                </div>
                            </div>
                        ))}
                        {(recent.orders || []).length === 0 && (
                            <div className="text-sm text-slate-500 py-6">No orders yet.</div>
                        )}
                    </div>
                </div>
                <div className="glass-card p-6" data-testid="recent-payments">
                    <div className="label-uppercase">Recent Payments</div>
                    <div className="mt-3 divide-y divide-white/5">
                        {(recent.payments || []).map((p) => (
                            <div key={p.id} className="py-3 flex items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm truncate">{p.party_name}</div>
                                    <div className="text-xs text-slate-500">
                                        {p.method.toUpperCase()} · {formatDateTime(p.date)}
                                    </div>
                                </div>
                                <div
                                    className={`font-display font-bold text-sm ${
                                        p.entry_type === "credit"
                                            ? "text-emerald-300"
                                            : "text-rose-300"
                                    }`}
                                >
                                    {p.entry_type === "credit" ? "+" : "−"}
                                    {formatCurrency(p.amount)}
                                </div>
                            </div>
                        ))}
                        {(recent.payments || []).length === 0 && (
                            <div className="text-sm text-slate-500 py-6">No payments yet.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
